import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../schema.dexie";
import {
  getCriancaAtiva,
  listarAlimentos,
  registrarOfertaCuidador,
  registrarOfertaProfissional,
} from "../db/queries";
import {
  ESPECIALIDADE_LABEL,
  type EspecialidadeProf,
  type PassoEscada,
} from "../types";
import { EscadaSelector } from "../ui/EscadaSelector";
import { FoodPhoto } from "../ui/FoodPhoto";
import { StatusBadge } from "../ui/StatusBadge";

/**
 * P0.2 — registro rápido (≤10s no caminho feliz): escolher alimento (recentes no
 * topo) → tocar o passo na escada → salvar. % comido é opcional e não bloqueia.
 */
export function RegistrarOferta() {
  const { alimentoId } = useParams();
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const alimentos = useLiveQuery(() => listarAlimentos()) ?? [];
  const ofertas = useLiveQuery(() => db.oferta.toArray()) ?? [];

  const [selecionado, setSelecionado] = useState<string | null>(alimentoId ?? null);
  const [passo, setPasso] = useState<PassoEscada | null>(null);
  const [pct, setPct] = useState<number | null>(null);
  const [salvo, setSalvo] = useState(false);
  // fluxo do registro: o dia a dia em casa vs. uma sessão de terapia (P/ confrontar)
  const [naTerapia, setNaTerapia] = useState(false);
  const [profNome, setProfNome] = useState("");
  const [profEsp, setProfEsp] = useState<EspecialidadeProf | "">("");

  // recentes no topo: pela data da última oferta; depois por criação.
  const ordenados = useMemo(() => {
    const ultima = new Map<string, string>();
    for (const o of ofertas) {
      const at = ultima.get(o.alimento_id);
      if (!at || o.data_hora > at) ultima.set(o.alimento_id, o.data_hora);
    }
    return [...alimentos].sort((a, b) => {
      const ua = ultima.get(a.id) ?? a.criado_em;
      const ub = ultima.get(b.id) ?? b.criado_em;
      return ub.localeCompare(ua);
    });
  }, [alimentos, ofertas]);

  const alimento = alimentos.find((a) => a.id === selecionado) ?? null;

  async function salvar() {
    if (!alimento || passo === null || !crianca) return;
    if (naTerapia) {
      await registrarOfertaProfissional({
        alimento_id: alimento.id,
        crianca_id: crianca.id,
        passo_escada: passo,
        porcao_comida_pct: pct,
        profissional_nome: profNome,
        profissional_especialidade: profEsp || null,
      });
    } else {
      await registrarOfertaCuidador({
        alimento_id: alimento.id,
        crianca_id: crianca.id,
        passo_escada: passo,
        porcao_comida_pct: pct,
      });
    }
    setSalvo(true);
    setTimeout(() => nav("/degrau", { replace: true }), 900);
  }

  if (salvo) {
    return (
      <main className="tela">
        <div className="vazio">
          <span className="emoji" aria-hidden>
            🌱
          </span>
          <div style={{ fontSize: "1.3rem", color: "var(--accent-forte)" }}>salvo!</div>
        </div>
      </main>
    );
  }

  // passo 1 — escolher alimento
  if (!alimento) {
    return (
      <main className="tela">
        <button className="btn btn-fantasma" onClick={() => nav(-1)}>
          ← voltar
        </button>
        <h1 className="tela-titulo">registrar oferta</h1>
        <p className="tela-sub">qual alimento você ofereceu?</p>
        {ordenados.length === 0 ? (
          <div className="vazio">
            <span className="emoji" aria-hidden>
              🍎
            </span>
            cadastre um alimento na biblioteca primeiro.
          </div>
        ) : (
          ordenados.map((a) => (
            <button
              className="card linha-alimento"
              key={a.id}
              onClick={() => setSelecionado(a.id)}
            >
              <FoodPhoto fotoId={a.fotos[0]} nome={a.nome} categoria={a.categoria} />
              <div className="crescer">
                <div className="nome">{a.nome}</div>
              </div>
              <StatusBadge status={a.status} />
            </button>
          ))
        )}
      </main>
    );
  }

  // passo 2 — escada + (opcional) % comido
  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => (alimentoId ? nav(-1) : setSelecionado(null))}>
        ← trocar alimento
      </button>

      <div className="row" style={{ margin: "var(--s2) 0 var(--s4)" }}>
        <FoodPhoto fotoId={alimento.fotos[0]} nome={alimento.nome} categoria={alimento.categoria} />
        <h1 className="tela-titulo" style={{ margin: 0 }}>
          {alimento.nome}
        </h1>
      </div>

      {/* onde foi? casa (dia a dia) vs terapia — pra confrontar os dois depois */}
      <div className="seg" role="group" aria-label="onde foi o registro">
        <button
          type="button"
          className={`seg-opt${naTerapia ? "" : " ativo"}`}
          aria-pressed={!naTerapia}
          onClick={() => setNaTerapia(false)}
        >
          🏠 em casa
        </button>
        <button
          type="button"
          className={`seg-opt${naTerapia ? " ativo" : ""}`}
          aria-pressed={naTerapia}
          onClick={() => setNaTerapia(true)}
        >
          🧩 na terapia
        </button>
      </div>

      {naTerapia && (
        <div className="card" style={{ marginBottom: "var(--s4)" }}>
          <div className="campo">
            <label>profissional <span style={{ color: "var(--text-suave)" }}>(opcional)</span></label>
            <input
              type="text"
              value={profNome}
              onChange={(e) => setProfNome(e.target.value)}
              placeholder="nome de quem conduziu"
            />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label>especialidade</label>
            <select
              value={profEsp}
              onChange={(e) => setProfEsp(e.target.value as EspecialidadeProf | "")}
            >
              <option value="">—</option>
              {(Object.keys(ESPECIALIDADE_LABEL) as EspecialidadeProf[]).map((k) => (
                <option key={k} value={k}>
                  {ESPECIALIDADE_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <h2 className="secao-titulo">como foi? toque o passo</h2>
      <EscadaSelector valor={passo} onChange={setPasso} />

      <section className="secao" style={{ marginTop: "var(--s5)" }}>
        <h2 className="secao-titulo">
          quanto comeu? <span style={{ color: "var(--text-suave)" }}>(opcional)</span>
        </h2>
        <div className="card">
          <div className="row" style={{ gap: "var(--s4)" }}>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={pct ?? 0}
              onChange={(e) => setPct(Number(e.target.value))}
              className="crescer"
              aria-label="porcentagem comida"
            />
            <span style={{ minWidth: 48, textAlign: "right", fontWeight: 500 }}>
              {pct === null ? "—" : `${pct}%`}
            </span>
          </div>
        </div>
      </section>

      <button
        className="btn btn-primario btn-bloco btn-grande"
        disabled={passo === null}
        onClick={salvar}
      >
        salvar oferta
      </button>
    </main>
  );
}
